import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '@/api/client';
import {
  getSignupConfig,
  updateSignupConfig,
  type SignupConfig,
  type UpdateSignupConfigInput,
} from '@/api/signups';
import type { TeamPanelHandle } from '@/components/teamPanelHandle';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { DatePickerPopover } from '@/components/ui/date-picker-popover';
import { TimePickerPopover } from '@/components/ui/time-picker-popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const pad2 = (n: number) => String(n).padStart(2, '0');

// Split a stored RFC3339 timestamp into the admin's LOCAL date (YYYY-MM-DD)
// and time (HH:MM) for the two inputs. The backend stores UTC; admins think
// in their own wall-clock, so we localize on display and re-UTC on save.
function isoToLocalParts(iso?: string): { date: string; time: string } {
  if (!iso) return { date: '', time: '' };
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { date: '', time: '' };
  return {
    date: `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`,
    time: `${pad2(d.getHours())}:${pad2(d.getMinutes())}`,
  };
}

// Recombine local date + time back into a UTC RFC3339 string. A blank date
// means "no bound" → null (the caller turns that into a clear_* flag). A
// blank time defaults to 00:00 so a date-only entry still produces a valid
// instant.
function localPartsToISO(date: string, time: string): string | null {
  if (!date) return null;
  const [y, m, day] = date.split('-').map(Number);
  if (!y || !m || !day) return null;
  const [hh, mm] = time ? time.split(':').map(Number) : [0, 0];
  const d = new Date(y, m - 1, day, hh ?? 0, mm ?? 0, 0, 0);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

// SignupConfigPanel renders the editable form for a team's signup_*
// configuration. Extracted from the old Signups.tsx so it can be reused
// inside TeamSheet's "Signup settings" tab.
//
// Owns its own data lifecycle: loads the config via React Query on mount,
// seeds local form state, PATCHes back on save. canManage gates write
// access (the backend handler enforces too).
//
// selectPortalContainer: where the Radix Select dropdowns portal into.
// Pass the sheet/dialog's content node when this is mounted inside a
// floating surface so the dropdown layers above it instead of behind.
export interface SignupConfigPanelProps {
  slug: string;
  teamID: string;
  canManage: boolean;
  selectPortalContainer?: HTMLElement | null | undefined;
}

export const SignupConfigPanel = forwardRef<TeamPanelHandle, SignupConfigPanelProps>(
  function SignupConfigPanel({ slug, teamID, canManage, selectPortalContainer }, ref) {
    const { t } = useTranslation('common');
    const auth = useAuth();
    const token = auth.user?.access_token ?? null;
    const queryClient = useQueryClient();

    const configQuery = useQuery({
      queryKey: ['signup-config', slug, teamID],
      queryFn: () => getSignupConfig(token, slug, teamID),
      enabled: !!token && !!slug && !!teamID,
    });

    // Hold the inner form's handle so we can forward commit() through
    // the loading-wrapper. Until the config has loaded, commit is a
    // no-op (nothing to save).
    const innerRef = (ref as React.RefObject<TeamPanelHandle>) ?? null;

    if (configQuery.isPending) {
      return (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      );
    }
    if (configQuery.isError || !configQuery.data) {
      return (
        <p role="alert" className="text-sm text-destructive">
          {t('signups.config.loadError')}
        </p>
      );
    }

    return (
      <SignupConfigForm
        ref={innerRef}
        slug={slug}
        teamID={teamID}
        config={configQuery.data}
        canManage={canManage}
        selectPortalContainer={selectPortalContainer}
        onSaved={() => {
          queryClient.invalidateQueries({
            queryKey: ['signup-config', slug, teamID],
          });
          queryClient.invalidateQueries({ queryKey: ['signup-summary', slug] });
          queryClient.invalidateQueries({ queryKey: ['teams', slug] });
        }}
      />
    );
  },
);

const SignupConfigForm = forwardRef<
  TeamPanelHandle,
  {
    slug: string;
    teamID: string;
    config: SignupConfig;
    canManage: boolean;
    selectPortalContainer?: HTMLElement | null | undefined;
    onSaved: () => void;
  }
>(function SignupConfigForm(
  { slug, teamID, config, canManage, selectPortalContainer, onSaved },
  ref,
) {
  const { t, i18n } = useTranslation('common');
  const auth = useAuth();
  const token = auth.user?.access_token ?? null;

  const [enabled, setEnabled] = useState(config.enabled);
  const [mode, setMode] = useState<'open' | 'admin_approval'>(
    config.mode === 'admin_approval' ? 'admin_approval' : 'open',
  );
  const [capacity, setCapacity] = useState<string>(
    config.capacity === null ? '' : String(config.capacity),
  );
  const [publicDescription, setPublicDescription] = useState(
    config.public_description,
  );
  const [requiresBirthDate, setRequiresBirthDate] = useState(
    config.requires_birth_date,
  );
  const [requiresPhone, setRequiresPhone] = useState(config.requires_phone);
  const [requiresGuardianWhenMinor, setRequiresGuardianWhenMinor] = useState(
    config.requires_guardian_when_minor,
  );
  const [requiresExperience, setRequiresExperience] = useState(
    config.requires_experience,
  );
  const [requiresShirtSize, setRequiresShirtSize] = useState(
    config.requires_shirt_size,
  );
  const [requiresPersonnummer, setRequiresPersonnummer] = useState(
    config.requires_personnummer,
  );
  const [consentText, setConsentText] = useState(config.consent_text);
  // Fee is stored in MAJOR units (kr / €) in the input so admins don't
  // have to think in minor units; converted to minor on submit. Empty
  // string = free signup.
  const [feeAmountMajor, setFeeAmountMajor] = useState<string>(
    config.fee_amount_minor === null
      ? ''
      : (config.fee_amount_minor / 100).toFixed(2),
  );
  const [feeCurrency, setFeeCurrency] = useState<
    'SEK' | 'NOK' | 'DKK' | 'EUR' | 'USD'
  >(config.fee_currency ?? 'SEK');
  const [waitlistCapacity, setWaitlistCapacity] = useState<string>(
    config.waitlist_capacity === null ? '' : String(config.waitlist_capacity),
  );
  // Age band + gender restriction — empty string / 'any' = unset.
  const [minBirthYear, setMinBirthYear] = useState<string>(
    config.min_birth_year === null ? '' : String(config.min_birth_year),
  );
  const [maxBirthYear, setMaxBirthYear] = useState<string>(
    config.max_birth_year === null ? '' : String(config.max_birth_year),
  );
  const [genderRestriction, setGenderRestriction] = useState<
    'any' | 'male' | 'female'
  >(config.gender_restriction ?? 'any');
  // Signup window — opens/closes are full timestamps split across a date +
  // time field each. Blank date = no bound on that side (open now / never
  // auto-close).
  const [opensDate, setOpensDate] = useState(() => isoToLocalParts(config.opens_at).date);
  const [opensTime, setOpensTime] = useState(() => isoToLocalParts(config.opens_at).time);
  const [closesDate, setClosesDate] = useState(() => isoToLocalParts(config.closes_at).date);
  const [closesTime, setClosesTime] = useState(() => isoToLocalParts(config.closes_at).time);

  // Reset on team switch (config prop identity changes).
  useEffect(() => {
    setEnabled(config.enabled);
    setMode(config.mode === 'admin_approval' ? 'admin_approval' : 'open');
    setCapacity(config.capacity === null ? '' : String(config.capacity));
    setPublicDescription(config.public_description);
    setRequiresBirthDate(config.requires_birth_date);
    setRequiresPhone(config.requires_phone);
    setRequiresGuardianWhenMinor(config.requires_guardian_when_minor);
    setRequiresExperience(config.requires_experience);
    setRequiresShirtSize(config.requires_shirt_size);
    setRequiresPersonnummer(config.requires_personnummer);
    setConsentText(config.consent_text);
    setFeeAmountMajor(
      config.fee_amount_minor === null
        ? ''
        : (config.fee_amount_minor / 100).toFixed(2),
    );
    setFeeCurrency(config.fee_currency ?? 'SEK');
    setWaitlistCapacity(
      config.waitlist_capacity === null ? '' : String(config.waitlist_capacity),
    );
    setMinBirthYear(
      config.min_birth_year === null ? '' : String(config.min_birth_year),
    );
    setMaxBirthYear(
      config.max_birth_year === null ? '' : String(config.max_birth_year),
    );
    setGenderRestriction(config.gender_restriction ?? 'any');
    setOpensDate(isoToLocalParts(config.opens_at).date);
    setOpensTime(isoToLocalParts(config.opens_at).time);
    setClosesDate(isoToLocalParts(config.closes_at).date);
    setClosesTime(isoToLocalParts(config.closes_at).time);
  }, [config]);

  const [saving, setSaving] = useState(false);
  const [errorCode, setErrorCode] = useState<string | null>(null);

  // Imperative commit — driven by TeamSheet's footer Save button.
  // No client-side validation here (the form's number inputs already
  // constrain input), so commit always returns true unless the server
  // rejects. Throws on server failure so the sheet surfaces it.
  useImperativeHandle(
    ref,
    () => ({
      commit: async () => {
        if (!canManage) return true; // read-only viewer, nothing to persist
        const input: UpdateSignupConfigInput = {
          enabled,
          mode,
          public_description: publicDescription,
          requires_birth_date: requiresBirthDate,
          requires_phone: requiresPhone,
          requires_guardian_when_minor: requiresGuardianWhenMinor,
          requires_experience: requiresExperience,
          requires_shirt_size: requiresShirtSize,
          requires_personnummer: requiresPersonnummer,
          consent_text: consentText,
        };
        const feeMajor = feeAmountMajor.trim();
        if (feeMajor === '') {
          input.clear_fee = true;
        } else {
          const major = Number(feeMajor.replace(',', '.'));
          if (Number.isFinite(major) && major > 0) {
            input.fee_amount_minor = Math.round(major * 100);
            input.fee_currency = feeCurrency;
          }
        }
        const waitlistTrimmed = waitlistCapacity.trim();
        if (waitlistTrimmed === '') {
          input.clear_waitlist = true;
        } else {
          const n = parseInt(waitlistTrimmed, 10);
          if (Number.isFinite(n) && n > 0) {
            input.waitlist_capacity = n;
          }
        }
        if (capacity === '') {
          input.clear_capacity = true;
        } else {
          const n = parseInt(capacity, 10);
          if (Number.isFinite(n) && n > 0) {
            input.capacity = n;
          }
        }
        // Age band — empty input means "no bound on that side". Out-of-
        // range values (validated server-side too) fall through to the
        // clear path so a fat-finger doesn't persist garbage.
        const trimmedMin = minBirthYear.trim();
        if (trimmedMin === '') {
          input.clear_min_birth_year = true;
        } else {
          const n = parseInt(trimmedMin, 10);
          if (Number.isFinite(n) && n >= 1900 && n <= 2100) {
            input.min_birth_year = n;
          } else {
            input.clear_min_birth_year = true;
          }
        }
        const trimmedMax = maxBirthYear.trim();
        if (trimmedMax === '') {
          input.clear_max_birth_year = true;
        } else {
          const n = parseInt(trimmedMax, 10);
          if (Number.isFinite(n) && n >= 1900 && n <= 2100) {
            input.max_birth_year = n;
          } else {
            input.clear_max_birth_year = true;
          }
        }
        if (genderRestriction === 'any') {
          input.clear_gender_restriction = true;
        } else {
          input.gender_restriction = genderRestriction;
        }
        // Signup window. Blank date → clear that bound; otherwise localize the
        // date + time into a UTC instant the backend stores verbatim.
        const opensISO = localPartsToISO(opensDate, opensTime);
        if (opensISO) {
          input.opens_at = opensISO;
        } else {
          input.clear_opens_at = true;
        }
        const closesISO = localPartsToISO(closesDate, closesTime);
        if (closesISO) {
          input.closes_at = closesISO;
        } else {
          input.clear_closes_at = true;
        }
        setSaving(true);
        setErrorCode(null);
        try {
          await updateSignupConfig(token, slug, teamID, input);
          onSaved();
          return true;
        } catch (err) {
          setErrorCode(err instanceof ApiError ? err.code : 'unknown');
          throw err;
        } finally {
          setSaving(false);
        }
      },
    }),
    [
      canManage,
      enabled,
      mode,
      publicDescription,
      requiresBirthDate,
      requiresPhone,
      requiresGuardianWhenMinor,
      requiresExperience,
      requiresShirtSize,
      requiresPersonnummer,
      consentText,
      feeAmountMajor,
      feeCurrency,
      waitlistCapacity,
      capacity,
      minBirthYear,
      maxBirthYear,
      genderRestriction,
      opensDate,
      opensTime,
      closesDate,
      closesTime,
      token,
      slug,
      teamID,
      onSaved,
    ],
  );

  return (
    <div className="flex flex-col gap-4">
      <header className="flex items-center justify-between">
        <h3 className="font-heading text-base font-semibold text-foreground">
          {t('signups.config.heading')}
        </h3>
        {saving && (
          <span className="text-sm text-muted-foreground">
            {t('common.submitting')}
          </span>
        )}
      </header>

      <label className="flex items-center gap-2 text-sm font-medium text-foreground">
        <Checkbox
          checked={enabled}
          onChange={(e) => setEnabled(e.target.checked)}
          disabled={!canManage}
        />
        {t('signups.config.enabled')}
      </label>

      <label className="flex flex-col gap-1.5 text-sm font-medium text-foreground">
        {t('signups.config.modeLabel')}
        <Select
          value={mode}
          onValueChange={(v) => setMode(v as 'open' | 'admin_approval')}
          disabled={!canManage}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent
            {...(selectPortalContainer
              ? { container: selectPortalContainer }
              : {})}
          >
            <SelectItem value="open">{t('signups.config.mode.open')}</SelectItem>
            <SelectItem value="admin_approval">
              {t('signups.config.mode.adminApproval')}
            </SelectItem>
          </SelectContent>
        </Select>
        <span className="text-xs text-muted-foreground">
          {mode === 'open'
            ? t('signups.config.modeHint.open')
            : t('signups.config.modeHint.adminApproval')}
        </span>
      </label>

      <label className="flex flex-col gap-1.5 text-sm font-medium text-foreground">
        {t('signups.config.publicDescription')}
        <textarea
          className="min-h-[80px] rounded-2xl border border-border bg-background px-3 py-2 text-sm"
          value={publicDescription}
          onChange={(e) => setPublicDescription(e.target.value)}
          maxLength={1000}
          disabled={!canManage}
          placeholder={t('signups.config.publicDescriptionPlaceholder')}
        />
      </label>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <label className="flex flex-col gap-1.5 text-sm font-medium text-foreground">
          {t('signups.config.capacity')}
          <Input
            type="number"
            min={1}
            value={capacity}
            onChange={(e) => setCapacity(e.target.value)}
            placeholder={t('signups.config.capacityUnlimited')}
            disabled={!canManage}
          />
          <span className="text-xs text-muted-foreground">
            {t('signups.config.capacityHint')}
          </span>
        </label>

        <label className="flex flex-col gap-1.5 text-sm font-medium text-foreground">
          {t('signups.config.waitlistCapacity')}
          <Input
            type="number"
            min={1}
            value={waitlistCapacity}
            onChange={(e) => setWaitlistCapacity(e.target.value)}
            placeholder={t('signups.config.waitlistCapacityPlaceholder')}
            disabled={!canManage || capacity === ''}
          />
          <span className="text-xs text-muted-foreground">
            {capacity === ''
              ? t('signups.config.waitlistCapacityHintNoMain')
              : t('signups.config.waitlistCapacityHint')}
          </span>
        </label>

      </div>

      <fieldset className="flex flex-col gap-3">
        <legend className="text-sm font-medium text-foreground">
          {t('signups.config.scheduleHeading')}
        </legend>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-foreground">
              {t('signups.config.opensLabel')}
            </span>
            <div className="flex items-start gap-2">
              <DatePickerPopover
                value={opensDate}
                onChange={setOpensDate}
                locale={i18n.language}
                disabled={!canManage}
                container={selectPortalContainer}
                clearable
                clearLabel={t('signups.config.scheduleClear')}
                placeholder={t('signups.config.scheduleDatePlaceholder')}
                className="flex-1"
              />
              <TimePickerPopover
                value={opensTime}
                onChange={setOpensTime}
                disabled={!canManage || opensDate === ''}
                ariaLabel={`${t('signups.config.opensLabel')} – ${t('signups.config.scheduleTime')}`}
                className="w-28 shrink-0"
              />
            </div>
          </div>
          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-foreground">
              {t('signups.config.closesLabel')}
            </span>
            <div className="flex items-start gap-2">
              <DatePickerPopover
                value={closesDate}
                onChange={setClosesDate}
                locale={i18n.language}
                disabled={!canManage}
                container={selectPortalContainer}
                clearable
                clearLabel={t('signups.config.scheduleClear')}
                placeholder={t('signups.config.scheduleDatePlaceholder')}
                min={opensDate || undefined}
                className="flex-1"
              />
              <TimePickerPopover
                value={closesTime}
                onChange={setClosesTime}
                disabled={!canManage || closesDate === ''}
                ariaLabel={`${t('signups.config.closesLabel')} – ${t('signups.config.scheduleTime')}`}
                className="w-28 shrink-0"
              />
            </div>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          {t('signups.config.scheduleHint')}
        </p>
      </fieldset>

      <fieldset className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <legend className="col-span-full text-sm font-medium text-foreground">
          {t('signups.config.applicantHeading')}
        </legend>
        <label className="flex flex-col gap-1.5 text-sm font-medium text-foreground">
          {t('signups.config.minBirthYear')}
          <Input
            type="number"
            inputMode="numeric"
            min={1900}
            max={2100}
            value={minBirthYear}
            onChange={(e) => setMinBirthYear(e.target.value)}
            placeholder={t('signups.config.birthYearPlaceholder')}
            disabled={!canManage}
          />
        </label>
        <label className="flex flex-col gap-1.5 text-sm font-medium text-foreground">
          {t('signups.config.maxBirthYear')}
          <Input
            type="number"
            inputMode="numeric"
            min={1900}
            max={2100}
            value={maxBirthYear}
            onChange={(e) => setMaxBirthYear(e.target.value)}
            placeholder={t('signups.config.birthYearPlaceholder')}
            disabled={!canManage}
          />
        </label>
        <label className="flex flex-col gap-1.5 text-sm font-medium text-foreground">
          {t('signups.config.gender')}
          <Select
            value={genderRestriction}
            onValueChange={(v) =>
              setGenderRestriction(v as 'any' | 'male' | 'female')
            }
            disabled={!canManage}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent
              {...(selectPortalContainer
                ? { container: selectPortalContainer }
                : {})}
            >
              <SelectItem value="any">{t('signups.config.gender_any')}</SelectItem>
              <SelectItem value="male">{t('signups.config.gender_male')}</SelectItem>
              <SelectItem value="female">{t('signups.config.gender_female')}</SelectItem>
            </SelectContent>
          </Select>
        </label>
        <p className="col-span-full text-xs text-muted-foreground">
          {t('signups.config.applicantHint')}
        </p>
      </fieldset>

      <fieldset className="grid grid-cols-1 gap-4 sm:grid-cols-[2fr_1fr]">
        <legend className="col-span-full text-sm font-medium text-foreground">
          {t('signups.config.feeHeading')}
        </legend>
        <label className="flex flex-col gap-1.5 text-sm font-medium text-foreground">
          {t('signups.config.feeAmount')}
          <Input
            type="number"
            min={0}
            step={0.01}
            value={feeAmountMajor}
            onChange={(e) => setFeeAmountMajor(e.target.value)}
            placeholder={t('signups.config.feeAmountPlaceholder')}
            disabled={!canManage}
            inputMode="decimal"
          />
        </label>
        <label className="flex flex-col gap-1.5 text-sm font-medium text-foreground">
          {t('signups.config.feeCurrency')}
          <Select
            value={feeCurrency}
            onValueChange={(v) =>
              setFeeCurrency(v as 'SEK' | 'NOK' | 'DKK' | 'EUR' | 'USD')
            }
            disabled={!canManage || feeAmountMajor.trim() === ''}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent
              {...(selectPortalContainer
                ? { container: selectPortalContainer }
                : {})}
            >
              <SelectItem value="SEK">SEK</SelectItem>
              <SelectItem value="NOK">NOK</SelectItem>
              <SelectItem value="DKK">DKK</SelectItem>
              <SelectItem value="EUR">EUR</SelectItem>
              <SelectItem value="USD">USD</SelectItem>
            </SelectContent>
          </Select>
        </label>
        <p className="col-span-full text-xs text-muted-foreground">
          {feeAmountMajor.trim() === ''
            ? t('signups.config.feeHintFree')
            : t('signups.config.feeHintPaid')}
        </p>
      </fieldset>

      <fieldset className="flex flex-col gap-2">
        <legend className="text-sm font-medium text-foreground">
          {t('signups.config.requireHeading')}
        </legend>
        <label className="flex items-center gap-2 text-sm">
          <Checkbox
            checked={requiresBirthDate}
            onChange={(e) => setRequiresBirthDate(e.target.checked)}
            disabled={!canManage}
          />
          {t('signups.config.requireBirthDate')}
        </label>
        <label className="flex items-center gap-2 text-sm">
          <Checkbox
            checked={requiresPhone}
            onChange={(e) => setRequiresPhone(e.target.checked)}
            disabled={!canManage}
          />
          {t('signups.config.requirePhone')}
        </label>
        <label className="flex items-center gap-2 text-sm">
          <Checkbox
            checked={requiresGuardianWhenMinor}
            onChange={(e) => setRequiresGuardianWhenMinor(e.target.checked)}
            disabled={!canManage}
          />
          {t('signups.config.requireGuardian')}
        </label>
        <label className="flex items-center gap-2 text-sm">
          <Checkbox
            checked={requiresExperience}
            onChange={(e) => setRequiresExperience(e.target.checked)}
            disabled={!canManage}
          />
          {t('signups.config.requireExperience')}
        </label>
        <label className="flex items-center gap-2 text-sm">
          <Checkbox
            checked={requiresShirtSize}
            onChange={(e) => setRequiresShirtSize(e.target.checked)}
            disabled={!canManage}
          />
          {t('signups.config.requireShirtSize')}
        </label>
        <label className="flex items-start gap-2 text-sm">
          <Checkbox
            checked={requiresPersonnummer}
            onChange={(e) => setRequiresPersonnummer(e.target.checked)}
            disabled={!canManage}
          />
          <span className="flex flex-col">
            {t('signups.config.requirePersonnummer')}
            <span className="text-xs text-muted-foreground">
              {t('signups.config.requirePersonnummerHint')}
            </span>
          </span>
        </label>
      </fieldset>

      <label className="flex flex-col gap-1.5 text-sm font-medium text-foreground">
        {t('signups.config.consentText')}
        <textarea
          className="min-h-[80px] rounded-2xl border border-border bg-background px-3 py-2 text-sm"
          value={consentText}
          onChange={(e) => setConsentText(e.target.value)}
          maxLength={1000}
          disabled={!canManage}
          placeholder={t('signups.config.consentTextPlaceholder')}
        />
        <span className="text-xs text-muted-foreground">
          {t('signups.config.consentTextHint')}
        </span>
      </label>

      {errorCode && (
        <p role="alert" className="text-sm text-destructive">
          {t(`signups.config.error.${errorCode}`, {
            defaultValue: t('signups.config.error.unknown', {
              defaultValue: t('teams.modal.error.unknown'),
            }),
          })}
        </p>
      )}
    </div>
  );
});
